import { useState, useCallback, useEffect, createContext, useContext, useRef } from 'react';
import { ethers } from 'ethers';

const WalletContext = createContext(null);

// Polygon network configs
const NETWORKS = {
  polygon: {
    chainId: '0x89',
    chainIdDecimal: 137,
    chainName: 'Polygon Mainnet',
    rpcUrls: ['https://polygon-rpc.com'],
    blockExplorerUrls: ['https://polygonscan.com'],
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 }
  },
  polygon_amoy: {
    chainId: '0x13882',
    chainIdDecimal: 80002,
    chainName: 'Polygon Amoy Testnet',
    rpcUrls: ['https://rpc-amoy.polygon.technology'],
    blockExplorerUrls: ['https://amoy.polygonscan.com'],
    nativeCurrency: { name: 'MATIC', symbol: 'MATIC', decimals: 18 }
  }
};

const SEED_CONTRACT_ADDRESS = process.env.REACT_APP_SEED_CONTRACT_ADDRESS || '0x7960cadfdbfcd6c02585b4de612317ced0ccad11';
const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)'
];

const WALLETCONNECT_PROJECT_ID = process.env.REACT_APP_WALLETCONNECT_PROJECT_ID;

export function WalletProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [balance, setBalance] = useState(null);
  const [seedBalance, setSeedBalance] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [walletType, setWalletType] = useState(null); // 'metamask' | 'walletconnect'
  const wcProviderRef = useRef(null);

  const isConnected = !!address;
  const isPolygon = chainId === 137 || chainId === 80002;
  const walletConnectAvailable = !!WALLETCONNECT_PROJECT_ID;

  // Read balances for a given ethers provider + address
  const fetchBalances = useCallback(async (provider, account) => {
    try {
      const nativeBalance = await provider.getBalance(account);
      setBalance(ethers.formatEther(nativeBalance));
    } catch {
      setBalance('0');
    }

    try {
      const contract = new ethers.Contract(SEED_CONTRACT_ADDRESS, ERC20_ABI, provider);
      const seedBal = await contract.balanceOf(account);
      const decimals = await contract.decimals();
      setSeedBalance(ethers.formatUnits(seedBal, decimals));
    } catch {
      setSeedBalance('0');
    }
  }, []);

  // ==================== METAMASK ====================

  const connectMetaMask = useCallback(async () => {
    setError(null);
    setConnecting(true);

    try {
      if (!window.ethereum) {
        setError('MetaMask not detected. Please install the MetaMask browser extension.');
        setConnecting(false);
        return false;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);

      if (accounts.length === 0) {
        setError('No accounts found. Please unlock MetaMask.');
        setConnecting(false);
        return false;
      }

      const account = accounts[0];
      const network = await provider.getNetwork();

      setAddress(account);
      setChainId(Number(network.chainId));
      setWalletType('metamask');
      await fetchBalances(provider, account);

      // Listen for changes
      window.ethereum.on('accountsChanged', (accts) => {
        if (accts.length === 0) { resetState(); }
        else { setAddress(accts[0]); }
      });
      window.ethereum.on('chainChanged', (id) => setChainId(parseInt(id, 16)));

      setConnecting(false);
      return true;
    } catch (err) {
      setError(err.message || 'Failed to connect MetaMask');
      setConnecting(false);
      return false;
    }
    // eslint-disable-next-line
  }, [fetchBalances]);

  // ==================== WALLETCONNECT V2 ====================

  const connectWalletConnect = useCallback(async () => {
    setError(null);
    setConnecting(true);

    if (!WALLETCONNECT_PROJECT_ID) {
      setError('WalletConnect is not configured. Project ID required.');
      setConnecting(false);
      return false;
    }

    try {
      // Dynamic import to avoid bundling if not used
      const { default: EthereumProvider } = await import('@walletconnect/ethereum-provider');

      const wcProvider = await EthereumProvider.init({
        projectId: WALLETCONNECT_PROJECT_ID,
        chains: [137],
        optionalChains: [80002],
        showQrModal: true,
        metadata: {
          name: 'DowUrk FramewUrk',
          description: 'DeFi Lending for Louisiana Entrepreneurs',
          url: 'https://dowurktoday.org',
          icons: ['https://dowurktoday.org/logo192.png']
        }
      });

      // Enable triggers the QR modal
      const accounts = await wcProvider.enable();

      if (!accounts || accounts.length === 0) {
        setError('No accounts returned from WalletConnect.');
        setConnecting(false);
        return false;
      }

      wcProviderRef.current = wcProvider;
      const provider = new ethers.BrowserProvider(wcProvider);
      const network = await provider.getNetwork();
      const account = accounts[0];

      setAddress(account);
      setChainId(Number(network.chainId));
      setWalletType('walletconnect');
      await fetchBalances(provider, account);

      // Listen for WC events
      wcProvider.on('accountsChanged', (accts) => {
        if (accts.length === 0) { resetState(); }
        else { setAddress(accts[0]); }
      });
      wcProvider.on('chainChanged', (id) => setChainId(id));
      wcProvider.on('disconnect', () => resetState());

      setConnecting(false);
      return true;
    } catch (err) {
      // User closed modal or connection failed
      if (err.message?.includes('User rejected') || err.message?.includes('dismissed')) {
        setError(null); // User cancelled — not an error
      } else {
        setError(err.message || 'WalletConnect connection failed');
      }
      setConnecting(false);
      return false;
    }
    // eslint-disable-next-line
  }, [fetchBalances]);

  // ==================== NETWORK SWITCHING ====================

  const switchToPolygon = useCallback(async (network = 'polygon_amoy') => {
    const config = NETWORKS[network];

    if (walletType === 'metamask' && window.ethereum) {
      try {
        await window.ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: config.chainId }]
        });
        return true;
      } catch (switchError) {
        if (switchError.code === 4902) {
          try {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [config]
            });
            return true;
          } catch {
            setError('Failed to add Polygon network');
            return false;
          }
        }
        setError('Failed to switch network');
        return false;
      }
    }

    if (walletType === 'walletconnect' && wcProviderRef.current) {
      try {
        await wcProviderRef.current.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: config.chainId }]
        });
        return true;
      } catch {
        setError('Failed to switch network via WalletConnect');
        return false;
      }
    }

    return false;
  }, [walletType]);

  // ==================== DISCONNECT ====================

  const resetState = useCallback(() => {
    setAddress(null);
    setChainId(null);
    setBalance(null);
    setSeedBalance(null);
    setError(null);
    setWalletType(null);
  }, []);

  const disconnect = useCallback(async () => {
    if (walletType === 'walletconnect' && wcProviderRef.current) {
      try {
        await wcProviderRef.current.disconnect();
      } catch {
        // Ignore disconnect errors
      }
      wcProviderRef.current = null;
    }

    if (walletType === 'metamask' && window.ethereum) {
      window.ethereum.removeAllListeners?.('accountsChanged');
      window.ethereum.removeAllListeners?.('chainChanged');
    }

    resetState();
  }, [walletType, resetState]);

  // ==================== REFRESH BALANCES ====================

  const refreshBalances = useCallback(async () => {
    if (!address) return;

    try {
      let provider;
      if (walletType === 'walletconnect' && wcProviderRef.current) {
        provider = new ethers.BrowserProvider(wcProviderRef.current);
      } else if (walletType === 'metamask' && window.ethereum) {
        provider = new ethers.BrowserProvider(window.ethereum);
      } else {
        return;
      }
      await fetchBalances(provider, address);
    } catch {
      // Silent fail on refresh
    }
  }, [address, walletType, fetchBalances]);

  const shortenAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const value = {
    address, chainId, balance, seedBalance, connecting, error,
    isConnected, isPolygon, walletType, walletConnectAvailable,
    connectMetaMask, connectWalletConnect, switchToPolygon,
    disconnect, refreshBalances, shortenAddress, NETWORKS
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallet must be used within WalletProvider');
  }
  return context;
}
